import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { INVITE_ONLY_PATH } from '../routes';
import './Navigation.css';

interface NavigationProps {
  onLoginClick?: () => void;
  onSignUpClick?: () => void;
  onConsoleClick?: () => void;
  onProductsClick?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({
  onLoginClick,
  onSignUpClick,
  onConsoleClick,
  onProductsClick,
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, signOut } = useAuth();

  const handleLoginClick = () => {
    setIsMenuOpen(false);
    if (onLoginClick) {
      onLoginClick();
    } else {
      navigate(INVITE_ONLY_PATH);
    }
  };

  const handleSignUpClick = () => {
    setIsMenuOpen(false);
    if (onSignUpClick) {
      onSignUpClick();
    } else if (onLoginClick) {
      onLoginClick();
    } else {
      navigate(INVITE_ONLY_PATH);
    }
  };

  const handleConsoleClick = () => {
    setIsMenuOpen(false);
    if (onConsoleClick) {
      onConsoleClick();
    } else {
      navigate('/dashboard/overview');
    }
  };

  const handleProductsClick = () => {
    setIsMenuOpen(false);
    if (onProductsClick) {
      onProductsClick();
    } else {
      navigate(INVITE_ONLY_PATH);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsMenuOpen(false);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside or pressing escape
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMenuOpen && !target.closest('.navigation') && !target.closest('.mobile-menu')) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (isMenuOpen && event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handlePricingClick = () => {
    setIsMenuOpen(false);
    navigate('/pricing');
  };

  const handleDocsClick = () => {
    setIsMenuOpen(false);
    navigate(INVITE_ONLY_PATH);
  };

  const navItems = [
    { name: 'Products', action: handleProductsClick },
    { name: 'Docs', action: handleDocsClick },
    { name: 'Pricing', action: handlePricingClick }
  ];

  return (
    <nav className={`navigation ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <div 
          className="nav-logo" 
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        >
          <span className="logo-dot">N</span>
          <span className="logo-text">Novembr</span>
        </div>

        <div className="nav-links">
          {navItems.map((item) => (
            <button key={item.name} onClick={item.action} className="nav-link">
              {item.name}
            </button>
          ))}
        </div>

        <div className="nav-cta">
          {user ? (
            <div className="nav-user">
              <button className="nav-console" onClick={handleConsoleClick}>Console</button>
              <button className="nav-logout" onClick={handleSignOut}>Logout</button>
            </div>
          ) : (
            <>
              <button className="nav-login" onClick={handleLoginClick}>Log in</button>
              <button className="nav-waitlist" onClick={handleSignUpClick}>
                Start Moderating <span className="arrow">→</span>
              </button>
            </>
          )}
        </div>

        {!isMenuOpen && (
          <button 
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Menu size={24} />
          </button>
        )}
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && createPortal(
        <div className="mobile-menu">
          <button 
            className="mobile-menu-close-btn"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={24} />
          </button>
          <div className="mobile-menu-content">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  item.action();
                  setIsMenuOpen(false);
                }}
                className="mobile-nav-link"
              >
                {item.name}
              </button>
            ))}
            {user ? (
              <div className="mobile-auth">
                <button className="nav-console mobile-console" onClick={handleConsoleClick}>Console</button>
                <button className="nav-logout mobile-logout" onClick={handleSignOut}>Logout</button>
              </div>
            ) : (
              <div className="mobile-auth">
                <button className="nav-login mobile-login" onClick={handleLoginClick}>Login</button>
                <button className="nav-signup mobile-signup" onClick={handleSignUpClick}>Start Moderating</button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
};

export default Navigation;
